import { Injectable } from "@angular/core";
import { VERSION } from "../const";
import { Displayable, Filter, filterGroup, StoredFilter } from "../models/filter-models";
import { DEFAULT_LABEL_SIZE_FACTORS, LabelSize, scaleLabelSize } from "../models/enums";
import { filterTemplate, filterPrefix, filterSuffix, filterHideFlasksByAreaLevel, filterHideScrolls, filterHideSingleScrolls, filterDefaultMinLabelSize, filterHideFlasksByAreaLevelPoe1LifeMana, filterHideFlasksByAreaLevelPoe1Hybrid } from "../filter-template";
import { GearGeneratorService } from "./generators/gear-generator.service";
import { CurrencyGeneratorService } from "./generators/currency-generator.service";
import { GeneralGeneratorService } from "./generators/general-generator.service";
import { UniqueGeneratorService } from "./generators/unique-generator.service";
import { poe1, poe2 } from "../../game";
import { HiddenItemsGeneratorService } from "./generators/hidden-items-generator.service";
import { PoE2WaystoneGeneratorService } from "./generators/poe2-waystone-generator.service";
import { PoE1MapGeneratorService } from "./generators/poe1-map-generator.service";
import { RecipeSalvagingGeneratorService } from "./generators/recipe-salvaging-generator.service";
import { IdentifiedItemGeneratorService } from "./generators/identified-item-generator.service";
import { LevelingGearGeneratorService } from "./generators/leveling-gear-generator.service";
import { Poe1CraftingBasesGeneratorService } from "./generators/poe1-crafting-bases-generator.service";
import { Poe2CraftingBasesGeneratorService } from "./generators/poe2-crafting-bases-generator.service";
import { CraftingBasesGeneratorService } from "./generators/crafting-bases-generator.service";
import { FlaskCharmGeneratorService } from "./generators/flask-charm-generator.service";
import { JewelleryGeneratorService } from "./generators/jewellery-generator.service";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

@Injectable({
  providedIn: 'root'
})
export class FilterGeneratorService {
  constructor(
    private generalGenerator: GeneralGeneratorService,
    private gearExceptionsGenerator: GearGeneratorService,
    private currencyGenerator: CurrencyGeneratorService,
    private poE1MapGenerator: PoE1MapGeneratorService,
    private poE2WaystoneGenerator: PoE2WaystoneGeneratorService,
    private uniqueRulesGenerator: UniqueGeneratorService,
    private hiddenItemsGenerator: HiddenItemsGeneratorService,
    private recipeSalvagingGenerator: RecipeSalvagingGeneratorService,
    private levelingGearGenerator: LevelingGearGeneratorService,
    private poe1CraftingBasesGenerator: Poe1CraftingBasesGeneratorService,
    private poe2CraftingBasesGenerator: Poe2CraftingBasesGeneratorService,
    private craftingBasesGenerator: CraftingBasesGeneratorService,
    private identifiedItemGenerator: IdentifiedItemGeneratorService,
    private flaskCharmGenerator: FlaskCharmGeneratorService,
    private jewelleryGenerator: JewelleryGeneratorService,
    private sanitizer: DomSanitizer
  ) {
  }

  public updateFilter(storedFilter: StoredFilter, disableCache: boolean = false): FilterResultData {
    const filter = storedFilter.filter;

    let filterText = '';
    let filterTextFull = '';

    const hiddenGearRules = this.hiddenItemsGenerator.generateHiddenGearRules(filter);
    const hiddenJewelleryRules = this.hiddenItemsGenerator.generateHiddenJewelleryRules(filter);
    const hiddenFlasksRules = this.hiddenItemsGenerator.generateHiddenFlasksRules(filter);

    const poe1MapRules = poe1 ?  this.poE1MapGenerator.generatePoE1MapRules(filter) : undefined;
    const poe2WaystoneFilterText = poe2 ? this.poE2WaystoneGenerator.generatePoE2WaystoneRules(filter) : '';

    const levelingGearRules = this.levelingGearGenerator.generateLevelingGearExceptionsRules(filter);

    let flaskAndCharmHideRules = filter.showLevelingFlasks
      ? poe2
        ? filterHideFlasksByAreaLevel
        : filterHideFlasksByAreaLevelPoe1LifeMana + (filter.showLevelingFlasksAlwaysHideHybrid ? '\n\n' + filterHideFlasksByAreaLevelPoe1Hybrid : '')
      : '';

    if (filter.ruthless) flaskAndCharmHideRules = flaskAndCharmHideRules.replaceAll('Hide ', 'Minimal ');

    let filterHideScrollsRules = (poe1 && filter.hideScrolls && filter.hideScrollsLowerThan)
      ? filter.hideScrollsLowerThan === 1
        ? filterHideSingleScrolls.replaceAll('{type}', 'Scroll of Wisdom')
        : filterHideScrolls.replace('{type}', 'Scroll of Wisdom').replace('{minScrolls}', (filter.hideScrollsLowerThan).toString())
      : '';

    if (filter.ruthless) filterHideScrollsRules = filterHideScrollsRules.replaceAll('Hide ', 'Minimal ');

    let filterHidePortalScrollsRules = (poe1 && filter.hidePortalScrolls && filter.hidePortalScrollsLowerThan)
      ? filter.hidePortalScrollsLowerThan === 1
        ? filterHideSingleScrolls.replaceAll('{type}', 'Portal Scroll')
        : filterHideScrolls.replace('{type}', 'Portal Scroll').replace('{minScrolls}', (filter.hidePortalScrollsLowerThan).toString())
      : '';

    if (filter.ruthless) filterHidePortalScrollsRules = filterHidePortalScrollsRules.replaceAll('Hide ', 'Minimal ');

    const { highlightRules: myGearHighlightRules, showRules: myGearShowRules } = this.gearExceptionsGenerator.generateMyGearRules(filter);
    const { highlightRules: myJewelleryHighlightRules, showRules: myJewelleryShowRules } = this.jewelleryGenerator.generateMyJewelleryRules(filter);
    const { highlightRules: myJewelsHighlightRules, showRules: myJewelsShowRules } = this.jewelleryGenerator.generateMyJewelsRules(filter);

    const unidentifiedGearRules = poe1 ? '' : this.gearExceptionsGenerator.generateUnidentifiedGearRules(filter);
    const unidentifiedJewelleryRules = poe1 ? '' : this.jewelleryGenerator.generateUnidentifiedJewelleryRules(filter);

    const influencedGearRules = poe2 ? '' : this.gearExceptionsGenerator.generateInfluencedGearRules(filter);

    const chromaticRecipeRules = poe2 ? '' : this.recipeSalvagingGenerator.generateChromaticRecipeExceptionsRules(filter);
    const jewellersRecipeRules = poe2 ? '' : this.recipeSalvagingGenerator.generateJewellersRecipeExceptionsRules(filter);
    const chaosRecipeRules = poe2 ? '' : this.recipeSalvagingGenerator.generateChaosRecipeExceptionsRules(filter);
    const exaltRecipeRules = poe2 ? '' : this.recipeSalvagingGenerator.generateExaltRecipeExceptionsRules(filter);

    const qualityGearRules = this.generalGenerator.generateQualityGearRules(filter);
    const socketedGearRules = this.generalGenerator.generateSocketedGearRules(filter);

    const goldRules = this.generalGenerator.generateGoldRules(filter);
    const verisiumRules = this.generalGenerator.generateVerisiumRules(filter);

    const chanceBaseHighlightRules = this.uniqueRulesGenerator.generateChanceBaseHighlightRules(filter);
    const uniquesHighlightRules = this.uniqueRulesGenerator.generateUniqueHighlightRules(filter);

    const questItemHighlightRules = this.generalGenerator.generateQuestItemHighlightRules(filter);
    const veiledJewelleryHighlightRules = poe2 ? '' : this.jewelleryGenerator.generateVeiledJewelleryHighlightRules(filter);
    const fracturedJewelleryHighlightRules = poe2 ? '' : this.jewelleryGenerator.generateFracturedJewelleryHighlightRules(filter);
    const synthesisedJewelleryHighlightRules = poe2 ? '' : this.jewelleryGenerator.generateSynthesisedJewelleryHighlightRules(filter);
    const influencedJewelleryHighlightRules = poe2 ? '' : this.jewelleryGenerator.generateInfluencedJewelleryHighlightRules(filter);
    const memoryStrandJewelleryHighlightRules = poe2 ? '' : this.jewelleryGenerator.generateMemoryStrandJewelleryHighlightRules(filter);
    const anointedAmuletsHighlightRules = this.jewelleryGenerator.generateAnointedAmuletsHighlightRules(filter);
    const specialGearHighlightRules = poe2 ? this.poe2CraftingBasesGenerator.generateSpecialGearHighlightRules(filter) : '';
    const corruptedImplicitJewelsHighlightRules = poe1 ? this.jewelleryGenerator.generateCorruptedImplicitJewelsHighlightRules(filter) : '';
    const corruptedImplicitJewelleryHighlightRules = poe1 ? this.jewelleryGenerator.generateCorruptedImplicitJewelleryHighlightRules(filter) : '';
    const clusterJewelHighlightRules = poe1 ? this.jewelleryGenerator.generateClusterJewelHighlightRules(filter) : '';
    const trialItemHighlightRules = this.generalGenerator.generateTrialItemHighlightRules(filter);
    const heistHighlightRules = this.generalGenerator.generateHeistHighlightRules(filter);
    const sixLinkedItemHighlightRules = this.generalGenerator.generate6LinkedItemRule(filter);
    const gemHighlightRules = this.generalGenerator.generateGemHighlightRules(filter);
    const craftingBaseHighlightRules = poe2 ? this.poe2CraftingBasesGenerator.generateExceptionalGearHighlightsRules(filter) : this.poe1CraftingBasesGenerator.generateCraftingBaseRules(filter);
    const iLevelCraftingBaseHighlightRules = poe2 ? this.poe2CraftingBasesGenerator.generateILevelCraftingBaseGearHighlightsRules(filter) : '';
    const otherSpecialJewelleryBaseTypesHighlightRules = (poe2 ? this.craftingBasesGenerator.generateExcellentAndGoodSpecialJewelleryBaseTypesRules(filter) : '')
      + this.craftingBasesGenerator.generateOtherSpecialJewelleryBaseTypesRules(filter);
    const highlightIdentifiedItemsRules = this.identifiedItemGenerator.generateIdentifiedItemsRules(filter);
    const restrictedModIdentifiedItemsRules = poe2 ? '' : this.identifiedItemGenerator.generateRestrictedModIdentifiedItemsRules(filter);
    const remainingIdentifiedGearRules = this.identifiedItemGenerator.generateRemainingIdentifiedGearRules(filter);
    const remainingIdentifiedJewelleryRules = this.identifiedItemGenerator.generateRemainingIdentifiedJewelleryRules(filter);

    const highILevelCharmsRules = this.flaskCharmGenerator.generateHighILevelCharmsRules(filter);
    const highILevelFlasksRules = this.flaskCharmGenerator.generateHighILevelFlasksRules(filter);
    const myFlaskRules = this.flaskCharmGenerator.generateMyFlaskRules(filter);
    const qualityFlaskRules = this.flaskCharmGenerator.generateQualityFlaskRules(filter);
    const qualityCharmsRules = this.flaskCharmGenerator.generateQualityCharmsRules(filter);

    const tieredCurrencyRules = this.currencyGenerator.generateTieredCurrencyRules(filter, disableCache);

    const customRules = this.generalGenerator.generateCustomRules(filter);
    const customCosmeticRules = this.generalGenerator.generateCustomCosmeticRules(filter);

    const factor = DEFAULT_LABEL_SIZE_FACTORS[filter.cosmeticPresets.defaultLabelSize];
    const minLabelSize = factor !== 1.0
      ? filterDefaultMinLabelSize.replace('{minLabelSize}', scaleLabelSize(LabelSize.Normal, filter.cosmeticPresets.defaultLabelSize).toString())
      : '';

    filterText = filterTemplate
      // General Partial Rules
      .replace('{minLabelSize}', minLabelSize)

      // Flask and Charm Filters
      .replace('{filterHideFlasksByAreaLevel}', flaskAndCharmHideRules)

      // Currency Filters
      .replace('{filterHideGold}', goldRules)
      .replace('{filterHideVerisium}', verisiumRules)
      .replace('{filterHideScrolls}', filterHideScrollsRules)
      .replace('{filterHidePortalScrolls}', filterHidePortalScrollsRules)

      // Jewellery Filters
      .replace('{filterHideJewellery}', hiddenJewelleryRules)

      // Highlights
      .replace('{filterHighlightUniques}', uniquesHighlightRules)
      .replace('{filterHighlightChanceBases}', chanceBaseHighlightRules)

      .replace('{filterHighlightQuestItems}', questItemHighlightRules)
      .replace('{filterHighlightVeiledJewellery}', veiledJewelleryHighlightRules)
      .replace('{filterHighlightSynthesisedJewellery}', synthesisedJewelleryHighlightRules)
      .replace('{filterHighlightFracturedJewellery}', fracturedJewelleryHighlightRules)
      .replace('{filterHighlightInfluencedJewellery}', influencedJewelleryHighlightRules)
      .replace('{filterHighlightMemoryStrandJewellery}', memoryStrandJewelleryHighlightRules)
      .replace('{filterHighlightAnointedAmulets}', anointedAmuletsHighlightRules)
      .replace('{filterHighlightSpecialGear}', specialGearHighlightRules)
      .replace('{filterHighlightCorruptedImplicitJewels}', corruptedImplicitJewelsHighlightRules)
      .replace('{filterHighlightCorruptedImplicitJewellery}', corruptedImplicitJewelleryHighlightRules)
      .replace('{filterHighlightClusterJewel}', clusterJewelHighlightRules)
      .replace('{filterHighlightTrialItems}', trialItemHighlightRules)
      .replace('{filterHighlightHeist}', heistHighlightRules)
      .replace('{filterHighlight6SocketedItems}', jewellersRecipeRules)
      .replace('{filterHighlight6LinkedItems}', sixLinkedItemHighlightRules)
      .replace('{filterHighlightGems}', gemHighlightRules)
      .replace('{filterHighlightCraftingBases}', craftingBaseHighlightRules)
      .replace('{filterHighlightHighILevelFlasks}', highILevelFlasksRules)
      .replace('{filterHighlightILevelCraftingBases}', iLevelCraftingBaseHighlightRules)
      .replace('{filterHighlightHighILevelCharms}', highILevelCharmsRules)
      .replace('{filterHighlightIdentifiedItems}', highlightIdentifiedItemsRules)
      .replace('{filterHighlightRestrictedModIdentifiedItems}', restrictedModIdentifiedItemsRules)
      .replace('{filterShowJewelleryShowSpecialBaseTypes}', otherSpecialJewelleryBaseTypesHighlightRules)

      .replace('{filterShowMyGearHighlights}', myGearHighlightRules)
      .replace('{filterShowMyJewelleryHighlights}', myJewelleryHighlightRules)
      .replace('{filterShowMyJewelsHighlights}', myJewelsHighlightRules)

      // Gear Filters
      .replace('{filterShowLevelingGear}', levelingGearRules)
      .replace('{filterShowQuality}', qualityGearRules)
      .replace('{filterShowQualityFlasks}', qualityFlaskRules)
      .replace('{filterShowQualityCharms}', qualityCharmsRules)
      .replace('{filterShowSocketed}', socketedGearRules)

      .replace('{filterUnidentifiedGear}', unidentifiedGearRules)
      .replace('{filterUnidentifiedJewellery}', unidentifiedJewelleryRules)
      .replace('{filterExaltRecipe}', exaltRecipeRules)
      .replace('{filterChaosRecipe}', chaosRecipeRules)
      .replace('{filterChromaticRecipe}', chromaticRecipeRules)
      .replace('{filterShowJewellersRecipe}', jewellersRecipeRules)

      .replace('{filterShowMyGear}', myGearShowRules)
      .replace('{filterShowMyJewellery}', myJewelleryShowRules)
      .replace('{filterShowMyJewels}', myJewelsShowRules)
      .replace('{filterShowMyFlasks}', myFlaskRules)
      .replace('{filterInfluencedGear}', influencedGearRules)

      .replace('{filterHideGear}', hiddenGearRules)
      .replace('{filterHideFlasksCharms}', hiddenFlasksRules)
      .replace('{filterHideRemainingIdentifiedGear}', remainingIdentifiedGearRules)
      .replace('{filterHideRemainingIdentifiedJewellery}', remainingIdentifiedJewelleryRules)

      // Dynamic Filters
      .replace('{filterWaystones}', poe1 ? (poe1MapRules?.filterText ?? '') : poe2WaystoneFilterText)

      // Currency
      .replace('{tieredCurrency}', tieredCurrencyRules)

    // Custom and Free Rules -- these must come after optionally removing all highlights and minimap icons
    filterText = filterText.replace('{filterFreeRulesTop}', filter.freeRulesTop ? filter.freeRulesTop : '')
      .replace('{filterFreeRulesBottom}', filter.freeRulesBottom ? filter.freeRulesBottom : '')
      .replace('{filterCustomRules}', customRules)
      .replace('{filterCustomCosmeticRules}', customCosmeticRules)
      // Cleanup
      .replaceAll(/(?:\r?\n){2,}/g, '\n\n');

    const filterConfig = JSON.stringify(filter);
    const filterConfigIndented = JSON.stringify(filter, null, 2);

    const filterVersion = VERSION;

    const filterConfigText = filterSuffix
      .replace('{version}', filterVersion)
      .replace('{date}', new Date().toISOString())
      .replace('{name}', storedFilter.name)
      .replace('{filterConfig}', filterConfigIndented.replaceAll('\n', '\n# '));

    filterTextFull = filterPrefix + filterText + filterConfigText;

    const resultData = new FilterResultData({
      filterText,
      filterConfig,
      filterTextFull,
      dynamicWaystoneThresholds: poe1MapRules?.dynamicWaystoneThresholds,
    });

    this.updateActiveFilterSections(filter, resultData);

    return resultData;
  }

  formatFilterTextWithRuleMarkers(filterText: string): SafeHtml {
    const escaped = filterText.replace(/[&<>]/g, c => c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;');

    // Process rule blocks: find lines starting with Show/Hide
    // and wrap the entire block plus individual components
    const lines = escaped.split('\n');
    const result: string[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Check for header pattern: separator line, title line, separator line
      // Pattern: line with many #, line with ### Title, line with many #
      const isSeparatorLine = /^#+$/.test(line.trim());

      if (isSeparatorLine && i + 2 < lines.length) {
        const titleLine = lines[i + 1];
        const nextSeparatorLine = lines[i + 2];
        const titleMatch = titleLine?.match(/^(###\s+)(.+)$/);
        const isNextSeparator = /^#+$/.test(nextSeparatorLine?.trim());

        if (titleMatch && isNextSeparator) {
          // Found a header block
          const [, titlePrefix, titleText] = titleMatch;
          const headerLines = [
            line,
            `${titlePrefix}<span class="rule-header-title">${titleText}</span>`,
            nextSeparatorLine
          ];

          const headerContent = headerLines.join('\n');
          const headerHtml = `<span class="rule-header">${headerContent}</span>`;
          result.push(headerHtml);

          // Skip the header lines and any blank lines after
          i += 3;
          while (i < lines.length && lines[i].trim() === '') {
            result.push('');
            i++;
          }
          continue;
        }
      }

      // Check if this line starts with Show or Hide
      const showHideMatch = line.match(/^(Show|Hide)(.*)/);

      if (showHideMatch) {
        const [, keyword, restOfLine] = showHideMatch;

        // Check if this line contains a rule ID pattern: # [title] (rule-id) or (rule-id/equipmentType/index)
        const ruleIdMatch = restOfLine.includes('#')
          ? restOfLine.match(/(.*?)(#)(\s*)([^\(]*?)(\s*)(\(([^\)]+)\))/)
          : null;

        // Collect the entire rule block (until next blank line)
        const ruleBlockLines: string[] = [];
        let wrappedRuleLine: string;
        let ruleIdValue: string | undefined;

        const keywordClass = keyword === 'Show' ? 'rule-keyword-show' : 'rule-keyword-hide';

        if (ruleIdMatch) {
          // Has rule ID: wrap Show/Hide, #, title, and rule-id
          const [, beforeHash, hashSign, space1, title, space2, ruleIdPart, ruleId] = ruleIdMatch;
          ruleIdValue = ruleId;

          const titleSpan = title.trim()
            ? `<span class="rule-title">${title.trim()}</span>`
            : '';

          wrappedRuleLine =
            `<span class="${keywordClass}">${keyword}</span>` +
            beforeHash +
            `<span class="rule-hash">${hashSign}</span>` +
            space1 +
            titleSpan +
            space2 +
            `<span class="rule-id">${ruleIdPart}</span>`;
        } else {
          // No rule ID: just wrap Show/Hide keyword
          wrappedRuleLine = `<span class="${keywordClass}">${keyword}</span>${restOfLine}`;
        }

        ruleBlockLines.push(wrappedRuleLine);
        i++;

        // Collect remaining lines of the rule block
        while (i < lines.length && lines[i].trim() !== '') {
          ruleBlockLines.push(lines[i]);
          i++;
        }

        // Wrap the entire rule block
        const ruleBlockContent = ruleBlockLines.join('\n');
        const ruleBlockHtml = ruleIdValue
          ? `<span class="rule-block" data-rule-id="${ruleIdValue}">${ruleBlockContent}</span>`
          : `<span class="rule-block">${ruleBlockContent}</span>`;
        result.push(ruleBlockHtml);

        // Add the blank line if we're not at the end
        if (i < lines.length) {
          result.push('');
          i++;
        }
      } else {
        // Not a rule block line, add as-is
        result.push(line);
        i++;
      }
    }

    const html = result.join('\n');

    // Use DomSanitizer to allow data-rule-id attributes and class attributes
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  updateActiveFilterSections(filter: Filter, resultData: FilterResultData) {
    for (let section in resultData.activeRules) {
      resultData.activeRules[section as filterGroup] = 0;
    }

    for (let section of Filter.sections) {
      for (let rule of section.rules) {
        if (poe1 && rule.poe2 || poe2 && rule.poe1) continue;
        if (filter[rule.key] === true) resultData.activeRules[section.group]++;
      }
    }

    // Special rules
    resultData.activeSpecialRules.valuableUniqueFilters =
      filter.uniqueTierCosmetics.filter(c => c.displayOptions?.active === true && c.uniqueTier !== 'Other').length;

    resultData.activeSpecialRules.remainingUniqueFilters =
      (filter.uniqueTierCosmetics.find(c => c.uniqueTier === 'Other')?.displayOptions?.active === true ? 1 : 0)
      + (poe2 && filter.showSmallUniques ? 1 : 0);

    resultData.activeSpecialRules.chanceBaseFilters =
      filter.chanceBaseTierCosmetics.filter(c => c.displayOptions?.active === true).length;

    resultData.activeSpecialRules.weaponExceptions =
      filter.weaponFilters.filter(c => c.show === true).length;

    resultData.activeSpecialRules.armourExceptions =
      filter.armourFilters.filter(c => c.show === true).length;

    resultData.activeSpecialRules.jewelleryExceptions =
      filter.jewelleryFilters.filter(c => c.show === true).length;

    resultData.activeSpecialRules.jewelExceptions =
      filter.jewelFilters.filter(c => c.show === true).length;

    resultData.activeSpecialRules.flaskExceptions =
      filter.flaskFilters.filter(c => c.show === true).length;

    resultData.activeSpecialRules.favouriteCurrencyTypes =
      filter.favouriteCurrencyTypes.filter(c => c.enabled).length;

    resultData.activeSpecialRules.identifiedItemConfigs =
      filter.identifiedItemConfigs.filter(c => c.enabled).length;

    if (filter.cosmeticSettings.stackedGeneralCurrencyPromotionEnabled) resultData.activeSpecialRules.currencyStackSettings++;
    if (filter.cosmeticSettings.stackedHiddenCurrencyPromotionEnabled) resultData.activeSpecialRules.currencyStackSettings++;
    if (filter.cosmeticSettings.stackedHiddenCurrencyNeverHighlight) resultData.activeSpecialRules.currencyStackSettings++;
  }
}

export type ActiveRules = {
  [K in filterGroup]: number;
};

export type ActiveSpecialRules = {
  currencyStackSettings: number;
  favouriteCurrencyTypes: number;
  weaponExceptions: number;
  armourExceptions: number;
  jewelleryExceptions: number;
  jewelExceptions: number;
  flaskExceptions: number;
  identifiedItemConfigs: number;
  valuableUniqueFilters: number;
  remainingUniqueFilters: number;
  chanceBaseFilters: number;
};

export class FilterResultData {
  constructor(init?: Partial<FilterResultData>) {
    if (init) {
      Object.assign(this, init);
    }
  }

  filterConfig!: string;
  filterText!: string;
  filterTextFull!: string;

  dynamicWaystoneThresholds?: Array<{ level: string }> = [];

  activeRules: ActiveRules = {
    levelingFilters: 0,
    vendorRecipeFiltersPoe1: 0,
    salvagingGearFiltersPoe2: 0,
    remainingFlaskCharmFilters: 0,
    favouriteCurrencyFilters: 0,
    currencyFilters: 0,
    unidentifiedEquipmentFiltersPoe2: 0,
    influencedGearFiltersPoe1: 0,
    influencedJewelleryFiltersPoe1: 0,
    specialEquipmentFiltersPoe2: 0,
    identifiedItemFilters: 0,
    craftingBaseFiltersPoe1: 0,
    craftingBaseFiltersPoe2: 0,
    remainingGearFilters: 0,
    remainingJewelleryFilters: 0,
    specialUniqueFilters: 0,
    gemFiltersPoe1: 0,
    mapFiltersPoe1: 0,
    otherItemFilters: 0,
  };

  activeSpecialRules: ActiveSpecialRules = {
    currencyStackSettings: 0,
    favouriteCurrencyTypes: 0,
    weaponExceptions: 0,
    armourExceptions: 0,
    jewelleryExceptions: 0,
    jewelExceptions: 0,
    flaskExceptions: 0,
    identifiedItemConfigs: 0,
    valuableUniqueFilters: 0,
    remainingUniqueFilters: 0,
    chanceBaseFilters: 0,
  }
};
